// BL-2078 — the after-submit re-check every React Hook Form form shares
// (ADR 016 — Form Validation § 8, clause 4). Renamed from BL-2165's
// `useSubmitGate`, minus its Save gate: validation never disables submit.

'use client';

import { useEffect } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { clientErrorPaths } from './client-error-paths';

/**
 * Keep a form's client errors honest after its first submit.
 *
 * After a submit RHF re-validates only the field that changed, so an error on
 * ANOTHER field — a row the change just hid, the far side of a cross-field
 * rule — outlives the change (BUG-1876). This hook re-runs the schema for
 * every field still in client error on each value change, so the messages on
 * screen always match the current values. It is a re-check, not a gate: the
 * submit control never reads validation state (ADR 016 § 8, clause 1), and
 * pressing it runs the full resolver and focuses the first error.
 *
 * A value change carries its field's `name`; the re-check's own update carries
 * none, so it cannot re-enter. (`subscribe`, not `watch`: the latter trips
 * `react-hooks/incompatible-library`.)
 *
 * @param form - The form, as `useForm` returned it.
 * @sideEffect Calls `form.trigger(...)` for stale client errors after a submit.
 */
export function useRevalidateAfterSubmit<TFieldValues extends FieldValues, TContext = unknown, TTransformedValues = TFieldValues>(
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>,
): void {
  const { subscribe, trigger } = form;
  useEffect(
    () =>
      subscribe({
        formState: { values: true },
        callback: ({ name, isSubmitted, errors }) => {
          if (name === undefined || !isSubmitted) return;
          const stale = clientErrorPaths(errors).filter((path) => path !== name);
          if (stale.length > 0) void trigger(stale as Path<TFieldValues>[]);
        },
      }),
    [subscribe, trigger],
  );
}
